import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

const TAG_FILTER_PARAM = 'filter.p.tag';
const ADOPTED_TITLE_FILTER_VALUE = 'adoptedtitle';
const ISBN_PARAM = 'filter.p.m.course_materials.isbn_ean';

function ensureAdoptedTitleFilter(searchParams) {
  const hasAdoptedTitle = searchParams.getAll(TAG_FILTER_PARAM).includes(ADOPTED_TITLE_FILTER_VALUE);

  if (!hasAdoptedTitle) {
    searchParams.append(TAG_FILTER_PARAM, ADOPTED_TITLE_FILTER_VALUE);
  }
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** @type {HTMLElement | null} Insert-after target when moving dropdown back from body (portal) */
  #dropdownPortalParent = null;

  /** @type {boolean} True when the most recent interaction before a focus event was a pointer (mouse/touch) */
  #lastFocusWasPointer = false;

  /** @type {HTMLElement | null} Element to return focus to when the mobile search modal closes */
  #returnFocusElement = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    // Track whether focus was initiated by a pointer so we can distinguish
    // keyboard Tab focus (should not auto-open the panel) from click/touch focus.
    this.addEventListener('pointerdown', () => { this.#lastFocusWasPointer = true; }, { signal });

    this.refs.searchInput.addEventListener(
      'focus',
      () => {
        this.#runAllPredictiveProductVisibilityChecks(this.refs.predictiveSearchResults);
      },
      { signal }
    );

    // Add form submit handler for ISBN searches
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.#handleFormSubmit, { signal });
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      // Inline header search: use data-desktop-search-open so layout change doesn't close panel on first click
      this.#bindInlineSearchOpenState(signal);
    }

    // Bind View All button directly (on:click may fail if dropdown is portaled outside component)
    if (this.refs.viewAllButton) {
      this.refs.viewAllButton.addEventListener('click', this.handleViewAllClick, { signal });
    }

    // Mobile full search: add/remove "mobile-search-opened" class on focus/blur
    if (this.classList.contains('search-mobile-full')) {
      this.#bindMobileSearchOpenState(signal);
    }
  }

  /**
   * Binds focus/blur handlers for mobile full search to add/remove "mobile-search-opened" class.
   * @param {AbortSignal} signal
   */
  #bindMobileSearchOpenState(signal) {
    const MOBILE_OPEN_CLASS = 'mobile-search-opened';

    this.refs.searchInput.addEventListener(
      'focus',
      (event) => {
        // Save the element to return focus to when the modal closes. Use the
        // element that had focus just before (relatedTarget), falling back to
        // the mobile search trigger button in the header.
        this.#returnFocusElement =
          event.relatedTarget instanceof HTMLElement
            ? event.relatedTarget
            : document.querySelector('[data-mobile-search-trigger]');

        // Only auto-open the full-screen modal on pointer (click/touch) focus.
        // Keyboard Tab focus must not trigger a change of context (WCAG 3.2.1).
        if (this.#lastFocusWasPointer) {
          this.classList.add(MOBILE_OPEN_CLASS);
        }
        this.#lastFocusWasPointer = false;
      },
      { signal }
    );

    // Open the modal when the user starts typing (covers keyboard-tab-then-type flow).
    this.refs.searchInput.addEventListener(
      'input',
      () => {
        this.classList.add(MOBILE_OPEN_CLASS);
      },
      { signal }
    );

    // Close the modal when focus leaves the component entirely (Tab past last element).
    this.addEventListener(
      'focusout',
      () => {
        // Small delay so the incoming focus event fires first, then check.
        window.setTimeout(() => {
          if (!this.#isFocusInsideComponent(document.activeElement) && this.classList.contains(MOBILE_OPEN_CLASS)) {
            this.classList.remove(MOBILE_OPEN_CLASS);
            const target = this.#returnFocusElement;
            this.#returnFocusElement = null;
            if (target instanceof HTMLElement) target.focus();
          }
        }, 100);
      },
      { signal }
    );

    // Handle cancel button click to close mobile search
    const closeButton = this.refs.closeModalButton ?? this.querySelector('.predictive-search__close-modal-button');
    if (closeButton) {
      closeButton.addEventListener(
        'click',
        () => {
          this.classList.remove(MOBILE_OPEN_CLASS);
          // Restore focus to the element that opened the search so screen
          // readers don't reset to the top of the page (WCAG 2.4.3).
          const target = this.#returnFocusElement;
          this.#returnFocusElement = null;
          if (target instanceof HTMLElement) {
            target.focus();
          } else {
            this.refs.searchInput.blur();
          }
        },
        { signal }
      );
    }
  }

  /**
   * Returns true if the given node is inside this component (including shadow DOM).
   * @param {Node | null} node
   * @returns {boolean}
   */
  #isFocusInsideComponent(node) {
    if (!node) return false;
    let current = node;
    while (current) {
      if (current === this) return true;
      current = current.parentNode || (current instanceof ShadowRoot ? current.host : null);
    }
    return false;
  }

  static DROPDOWN_PORTAL_CLASS = 'predictive-search-dropdown--portal';

  #moveDropdownToBody() {
    const wrapper = this.querySelector('.predictive-search-form__content-wrapper');
    const form = this.refs.form;
    if (!wrapper || !form || wrapper.classList.contains(PredictiveSearchComponent.DROPDOWN_PORTAL_CLASS)) return;
    const header = form.querySelector('.predictive-search-form__header');
    if (!header) return;
    this.#dropdownPortalParent = header;
    wrapper.classList.add(PredictiveSearchComponent.DROPDOWN_PORTAL_CLASS);
    document.body.appendChild(wrapper);
  }

  #moveDropdownBack() {
    const wrapper = document.body.querySelector(`.predictive-search-form__content-wrapper.${PredictiveSearchComponent.DROPDOWN_PORTAL_CLASS}`);
    if (!wrapper || !this.#dropdownPortalParent) return;
    wrapper.classList.remove(PredictiveSearchComponent.DROPDOWN_PORTAL_CLASS);
    this.#dropdownPortalParent.insertAdjacentElement('afterend', wrapper);
    this.#dropdownPortalParent = null;
  }

  /**
   * Binds open state for inline header search using data-desktop-search-open
   * so layout change on expand doesn't trigger blur and close immediately.
   * Dropdown top uses body’s existing --header-height only (no extra JS or vars).
   * @param {AbortSignal} signal
   */
  #bindInlineSearchOpenState(signal) {
    let closeTimeoutId = 0;
    let openedAt = 0;
    const DESKTOP_OPEN_ATTR = 'data-desktop-search-open';
    const BLUR_CLOSE_DELAY_MS = 180;
    const OPEN_CLICK_IGNORE_MS = 150;

    const closePanel = () => {
      this.removeAttribute(DESKTOP_OPEN_ATTR);
      if (this.refs.searchInput) this.refs.searchInput.blur();
    };

    const scheduleCloseIfFocusLeft = () => {
      closeTimeoutId = window.setTimeout(() => {
        closeTimeoutId = 0;
        if (!this.#isFocusInsideComponent(document.activeElement)) {
          closePanel();
        }
      }, BLUR_CLOSE_DELAY_MS);
    };

    const isDesktopViewport = () => window.matchMedia('(min-width: 1201px)').matches;

    const openPanel = () => {
      if (isDesktopViewport()) {
        this.setAttribute(DESKTOP_OPEN_ATTR, 'true');
        openedAt = Date.now();
      } else {
        this.removeAttribute(DESKTOP_OPEN_ATTR);
        requestAnimationFrame(() => {
          const wrapper = this.querySelector('.predictive-search-form__content-wrapper');
          if (wrapper) wrapper.style.top = '';
        });
      }
    };

    this.refs.searchInput.addEventListener(
      'focus',
      () => {
        if (closeTimeoutId) {
          clearTimeout(closeTimeoutId);
          closeTimeoutId = 0;
        }
        // Only auto-open the panel on pointer (click/touch) focus.
        // Keyboard Tab focus must not trigger a change of context (WCAG 3.2.1).
        // The panel will open when the user starts typing (see 'input' listener below).
        if (!this.#lastFocusWasPointer) {
          this.#lastFocusWasPointer = false;
          return;
        }
        this.#lastFocusWasPointer = false;
        /* Do not fetch empty state on focus: Liquid already renders Popular Searches + Featured Products
           in predictive-search-empty-state. Replacing that with API HTML caused stutter/flash in Safari. */
        openPanel();
      },
      { signal }
    );

    // Also open the panel when the user starts typing after keyboard-tab-to-input.
    this.refs.searchInput.addEventListener(
      'input',
      () => {
        if (!this.hasAttribute(DESKTOP_OPEN_ATTR) && isDesktopViewport()) {
          this.setAttribute(DESKTOP_OPEN_ATTR, 'true');
          openedAt = Date.now();
        }
      },
      { signal }
    );

    // Use focusout on the component (not just searchInput blur) so the panel
    // closes when Tab moves focus from any child (reset button, links, etc.) to outside.
    this.addEventListener('focusout', () => scheduleCloseIfFocusLeft(), { signal });

    document.addEventListener(
      'click',
      (e) => {
        if (openedAt && Date.now() - openedAt < OPEN_CLICK_IGNORE_MS) return;
        const clickedInside = e.composedPath().includes(this);
        if (!clickedInside) {
          scheduleCloseIfFocusLeft();
        }
      },
      { signal }
    );

    const closeButton = this.refs.closeModalButton ?? this.querySelector('.predictive-search__close-modal-button');
    if (closeButton) {
      closeButton.addEventListener(
        'click',
        () => {
          if (closeTimeoutId) clearTimeout(closeTimeoutId);
          closeTimeoutId = 0;
          closePanel();
        },
        { signal }
      );
    }
  }

  /**
   * Handle form submission - redirect to ISBN search if applicable
   * @param {Event} event - The form submit event
   */
  #handleFormSubmit = (event) => {
    const searchTerm = this.refs.searchInput.value.trim();

    if (this.#isISBN(searchTerm)) {
      event.preventDefault();
      const searchUrl = new URL('/search', location.origin);
      searchUrl.searchParams.set(
        'filter.p.m.course_materials.isbn_ean',
        searchTerm
      );
      searchUrl.searchParams.set('q', '*');
      window.location.href = searchUrl.toString();
    }
    // Otherwise, let the form submit naturally to routes.search_url
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Check if search term is an ISBN (10 or 13 digits)
   * @param {string} searchTerm - The search term to check
   * @returns {boolean} True if ISBN-10 or ISBN-13
   */
  #isISBN(searchTerm) {
    const trimmed = searchTerm.trim();
    return /^\d{10}$/.test(trimmed) || /^\d{13}$/.test(trimmed);
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const searchTerm = this.refs.searchInput.value.trim();

        // If ISBN, always navigate to search results page with ISBN filter
        if (this.#isISBN(searchTerm)) {
          event.preventDefault();
          const searchUrl = new URL('/search', location.origin);
          searchUrl.searchParams.set(
            'filter.p.m.course_materials.isbn_ean',
            searchTerm
          );
          searchUrl.searchParams.set('q', '*');
          searchUrl.searchParams.set('filter.v.availability', '1');
          window.location.href = searchUrl.toString();
          return;
        }

        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        }
        // Otherwise, let the form submit naturally
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Handles the View All button click.
   * Navigates to the full search results page with the current search term and availability filter.
   */
  handleViewAllClick = () => {
    const searchTerm = this.refs.searchInput.value.trim();
    const searchUrl = new URL(Theme.routes.search_url, location.origin);

    // If ISBN, always navigate to search results page with ISBN filter
    if (this.#isISBN(searchTerm)) {
      searchUrl.searchParams.set(
        'filter.p.m.course_materials.isbn_ean',
        searchTerm
      );
      searchUrl.searchParams.set('q', '*');
    } else {
      if (searchTerm.length > 0) {
        searchUrl.searchParams.set('q', searchTerm);
      }
      searchUrl.searchParams.set('filter.v.availability', '1');
    }
    
    window.location.href = searchUrl.toString();
  };

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();

    // For ISBN, fetch real search results using metafield filter
    if (this.#isISBN(searchTerm)) {
      this.#getISBNSearchResults(searchTerm);
      return;
    }

    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,product_type,vendor,tag,body');
    url.searchParams.set('filter.v.availability', '1');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
        this.#runAllPredictiveProductVisibilityChecks(predictiveSearchResults);
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch ISBN search results using the regular search API with metafield filter.
   * Uses Section Rendering API to get results that can be rendered in the dropdown.
   * @param {string} isbn - The ISBN to search for (10 or 13 digits)
   */
  async #getISBNSearchResults(isbn) {
    if (!this.dataset.sectionId) return;

    // Use regular search URL (not predictive search) because metafield filters
    // only work with /search, not /search/suggest
    const url = new URL(Theme.routes.search_url, location.origin);

    // Use wildcard query with ISBN metafield filter
    url.searchParams.set('q', '*');
    url.searchParams.set(ISBN_PARAM, isbn);
    url.searchParams.set('filter.v.availability', '1');
    url.searchParams.set('type', 'product');
    ensureAdoptedTitleFilter(url.searchParams);

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    try {
      const resultsMarkup = await sectionRenderer.getSectionHTML(
        this.dataset.sectionId,
        false,
        url
      );

      if (!resultsMarkup || abortController.signal.aborted) return;

      morph(predictiveSearchResults, resultsMarkup);
      this.#resetScrollPositions();

      const noResultsEl = predictiveSearchResults.querySelector('.predictive-search-results__no-results');
      const hasIsbnProducts = predictiveSearchResults.querySelector('.predictive-search-results__products[data-is-isbn-search="true"]');
      if (noResultsEl && !hasIsbnProducts) {
        console.log('[Predictive Search ISBN] No-results message shown (API returned 0):', {
          flow: 'predictive-search-isbn-api-zero-results',
          displayText: noResultsEl.textContent?.trim(),
          isbn: url?.searchParams?.get(ISBN_PARAM) || ''
        });
      }

      this.#runAllPredictiveProductVisibilityChecks(predictiveSearchResults, url);
    } catch (error) {
      if (abortController.signal.aborted) return;
      // On error, fall back to the empty ISBN dropdown
      this.#showISBNDropdown();
      throw error;
    }
  }

  /**
   * Re-triggers `search-element-slide-up` on product cards after the PDP grid is visible.
   * While `.pdp-visibility-pending` is on, the grid is `visibility:hidden` and the section
   * animation would finish off-screen; restarting after reveal fixes first-open Featured.
   */
  #scheduleProductCardSlideAfterReveal(productsEl) {
    if (!productsEl) return;
    if (prefersReducedMotion()) return;
    if (productsEl.getAttribute('data-is-isbn-search') === 'true') return;
    if (productsEl.getAttribute('data-pdp-visibility-empty') === 'true') return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#restartPredictiveSearchProductCardSlideAnimations(productsEl);
      });
    });
  }

  /**
   * @param {HTMLElement} productsEl
   */
  #restartPredictiveSearchProductCardSlideAnimations(productsEl) {
    if (!productsEl) return;

    const cards = productsEl.querySelectorAll(
      '.predictive-search-results__wrapper-products .predictive-search-results__card'
    );

    for (const card of cards) {
      card.style.animation = 'none';
      void card.offsetWidth;
      card.style.removeProperty('animation');
    }
  }

  /**
   * Run PDP visibility checks on every predictive product grid that ships a JSON payload
   * (Featured / Products / ISBN / recently viewed). Hides individual cards that fail; hides
   * the whole grid when none remain. ISBN grids keep the existing no-results message behavior.
   * @param {HTMLElement} container - The predictive search results container (post-morph)
   * @param {URL} [requestUrl] - Request URL (ISBN flows use it for live region text)
   */
  async #runAllPredictiveProductVisibilityChecks(container, requestUrl) {
    if (!container) return;

    const payloadScripts = [...container.querySelectorAll('script[data-predictive-pdp-visibility-payload]')];

    const revealPdpGrid = (productsEl) => {
      productsEl.classList.remove('pdp-visibility-pending');
      this.#scheduleProductCardSlideAfterReveal(productsEl);
    };

    const clearIsbnPending = () => {
      container.querySelectorAll('.predictive-search-results__products.isbn-results-pending').forEach((el) => {
        el.classList.remove('isbn-results-pending');
      });
    };

    const clearPdpPending = () => {
      container.querySelectorAll('.predictive-search-results__products.pdp-visibility-pending').forEach((el) => {
        revealPdpGrid(el);
      });
    };

    if (payloadScripts.length === 0) {
      clearIsbnPending();
      clearPdpPending();
      return;
    }

    const hasPendingVisibility =
      container.querySelector('.predictive-search-results__products.pdp-visibility-pending') ||
      container.querySelector('.predictive-search-results__products.isbn-results-pending');

    if (!hasPendingVisibility) {
      return;
    }

    const scriptUrl = this.dataset.pdpVisibilityUrl;
    if (!scriptUrl) {
      clearIsbnPending();
      clearPdpPending();
      return;
    }

    let run;
    try {
      const module = await import(/* @vite-ignore */ scriptUrl);
      run = module.runPdpVisibilityCheck || module.default;
    } catch {
      clearIsbnPending();
      clearPdpPending();
      return;
    }

    const processBlock = async (payloadEl) => {
      const productsEl = payloadEl.closest('.predictive-search-results__products');
      if (!productsEl) return;

      const isIsbn = productsEl.getAttribute('data-is-isbn-search') === 'true';

      let payloads;
      try {
        payloads = JSON.parse(payloadEl.textContent?.trim() || '[]');
      } catch {
        productsEl.classList.remove('isbn-results-pending');
        revealPdpGrid(productsEl);
        return;
      }

      if (!Array.isArray(payloads) || payloads.length === 0) {
        if (!isIsbn) {
          productsEl.setAttribute('data-pdp-visibility-empty', 'true');
        }
        productsEl.classList.remove('isbn-results-pending');
        revealPdpGrid(productsEl);
        return;
      }

      const cardsByProductId = {};
      productsEl.querySelectorAll('li[data-product-id]').forEach((card) => {
        const id = card.getAttribute('data-product-id');
        if (id) cardsByProductId[id] = card;
      });

      try {
        const checks = payloads.map((p) => {
          if (p.skipCheck === true) {
            return Promise.resolve({
              productId: p.productId,
              productHandle: p.productHandle,
              productTitle: p.productTitle,
              shouldDisplay: true,
              reason: 'adopted_supply_skip'
            });
          }
          return run(p, { logVerbose: false }).then((r) => ({
            productId: p.productId,
            productHandle: p.productHandle,
            productTitle: p.productTitle,
            shouldDisplay: r && r.shouldDisplay === true,
            reason: r ? r.reason : 'unknown',
            failedChecks: r ? r.failedChecks : []
          }));
        });

        const results = await Promise.all(checks);

        const initialCount = payloads.length;
        const filteredOut = [];

        results.forEach((r) => {
          const card = cardsByProductId[String(r.productId)];
          if (card && !r.shouldDisplay) {
            card.setAttribute('data-isbn-visibility-hidden', 'true');
            filteredOut.push({
              productId: r.productId,
              handle: r.productHandle,
              title: r.productTitle,
              reason: r.reason,
              failedChecks: r.failedChecks || []
            });
          }
        });

        const finalCount = initialCount - filteredOut.length;

        const logPrefix = isIsbn ? '[Predictive Search ISBN]' : '[Predictive Search PDP]';
        console.log(`${logPrefix} Visibility filter:`, {
          flow: isIsbn ? 'predictive-search-isbn-visibility-filtered' : 'predictive-search-pdp-visibility-filtered',
          initialResults: initialCount,
          finalResults: finalCount,
          filteredOutCount: filteredOut.length,
          filteredOut: filteredOut.length > 0 ? filteredOut : undefined
        });

        if (finalCount === 0) {
          if (isIsbn) {
            const noResultsEl = container.querySelector('#predictive-search-isbn-no-results');
            if (noResultsEl) {
              noResultsEl.style.display = '';
              console.log('[Predictive Search ISBN] No-results message shown (all filtered by visibility):', {
                flow: 'predictive-search-isbn-visibility-filtered',
                displayText: noResultsEl.textContent?.trim(),
                isbn: requestUrl?.searchParams?.get(ISBN_PARAM) || ''
              });
            }
            const productsUl = productsEl.querySelector('.predictive-search-results__wrapper-products');
            if (productsUl) productsUl.style.display = 'none';
            const loadingEl = productsEl.querySelector('.predictive-search-isbn-loading');
            if (loadingEl) loadingEl.style.display = 'none';
            const viewAll = productsEl.querySelector('.predictive-search-results__view-all');
            if (viewAll) viewAll.style.display = 'none';
          } else {
            productsEl.setAttribute('data-pdp-visibility-empty', 'true');
          }
        }

        const statusEl = container.querySelector('[role="status"][aria-live]');
        if (statusEl && isIsbn && requestUrl?.searchParams?.has(ISBN_PARAM)) {
          const isbn = requestUrl.searchParams.get(ISBN_PARAM) || '';
          statusEl.textContent =
            finalCount === 0
              ? 'No results found for ' + isbn
              : finalCount + ' result' + (finalCount === 1 ? '' : 's') + ' for ' + isbn;
        }
      } catch {
        // On error, show all products
      } finally {
        productsEl.classList.remove('isbn-results-pending');
        revealPdpGrid(productsEl);
      }
    };

    await Promise.all(payloadScripts.map((el) => processBlock(el)));
  }

  /**
   * Show empty dropdown for ISBN searches (used as fallback on error)
   */
  #showISBNDropdown() {
    const { predictiveSearchResults } = this.refs;

    const isbnDropdownMarkup = `
      <div
        id="predictive-search-results"
        class="predictive-search-dropdown"
        role="listbox"
        aria-label="Search results"
        aria-expanded="true"
        data-transition-container
      >
        <div class="predictive-search-results__inner" data-search-results>
          <div class="visually-hidden" role="status" aria-live="polite">
            Searching for ISBN...
          </div>
        </div>
      </div>
    `;

    morph(predictiveSearchResults, isbnDropdownMarkup);
    this.#resetScrollPositions();
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    // Ensure availability filter is always set to "In stock" (value "1")
    url.searchParams.set('filter.v.availability', '1');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    this.#currentIndex = -1;
    this.refs.searchInput.value = '';
    this.#hideResetButton();
    await this.#loadEmptyStateContent();
  };

  /**
   * Load empty state (Popular Searches + Featured Products) into results container.
   * Used on reset and when focusing inline search with empty input.
   */
  #loadEmptyStateContent = async () => {
    const { predictiveSearchResults } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    const abortController = this.#createAbortController();
    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();

      if (recentlyViewedMarkup) {
        if (abortController.signal.aborted) return;

        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');

        if (recentlyViewedProductsHtml) {
          for (const child of recentlyViewedProductsHtml.children) {
            if (child instanceof HTMLElement) {
              child.setAttribute('ref', 'recentlyViewedWrapper');
            }
          }

          const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');

          if (collectionElement) {
            collectionElement.replaceChildren(...recentlyViewedProductsHtml.children);
          }
        }
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
    this.#runAllPredictiveProductVisibilityChecks(predictiveSearchResults);
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}